/** Bundled icon sets (Maki, Temaki, Lucide).
 *
 *  The catalogues are static JSON in public/iconsets/, generated by
 *  `npm run iconsets`. They total ~1.2 MB, so they are fetched per set on
 *  demand rather than bundled: the kiosk pulls only the set(s) its categories
 *  actually reference, and the admin picker pulls a set when you open its tab.
 *
 *  An icon id is "<set>:<name>" (e.g. "maki:restaurant"). Ids with no colon
 *  are the legacy hand-drawn set in icons.jsx and resolve without a fetch.
 */

export const SETS = [
  { id: "maki", label: "Maki", note: "CC0" },
  { id: "temaki", label: "Temaki", note: "CC0" },
  { id: "lucide", label: "Lucide", note: "ISC" },
];

const SET_IDS = new Set(SETS.map((s) => s.id));

const catalogs = {}; // set id -> { name: {v,s,b,k} }, once loaded
const inFlight = {}; // set id -> Promise, so concurrent callers share one fetch
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn());

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Loaded catalogue for a set, or null while it is still in flight. */
export const getSet = (set) => catalogs[set] ?? null;

/** Kick off (or join) the fetch for a set. Resolves to the catalogue. */
export function ensureSet(set) {
  if (!SET_IDS.has(set)) return Promise.resolve(null);
  if (catalogs[set]) return Promise.resolve(catalogs[set]);
  inFlight[set] ??= fetch(`${import.meta.env.BASE_URL}iconsets/${set}.json`)
    .then((r) => {
      if (!r.ok) throw new Error(`iconset ${set}: ${r.status}`);
      return r.json();
    })
    .then((data) => {
      catalogs[set] = data;
      emit();
      return data;
    })
    .catch((e) => {
      // A missing catalogue must not take the map down — callers fall back to
      // the built-in set. Clear the slot so a later attempt can retry.
      delete inFlight[set];
      console.warn(e);
      return null;
    });
  return inFlight[set];
}

/** "maki:restaurant" -> {set,name}; null for legacy/built-in ids. */
export function parseIconId(id) {
  if (typeof id !== "string") return null;
  const at = id.indexOf(":");
  if (at < 0) return null;
  const set = id.slice(0, at);
  return SET_IDS.has(set) ? { set, name: id.slice(at + 1) } : null;
}

export const isBundledIcon = (id) => parseIconId(id) !== null;

/** Synchronous lookup — returns undefined until the set has loaded. */
export function getIcon(id) {
  const parsed = parseIconId(id);
  return parsed ? catalogs[parsed.set]?.[parsed.name] : undefined;
}

/** Async lookup for non-React callers (the map image rasteriser). */
export async function resolveIcon(id) {
  const parsed = parseIconId(id);
  if (!parsed) return null;
  const catalog = await ensureSet(parsed.set);
  return catalog?.[parsed.name] ?? null;
}

/** Preload every set referenced by a list of icon ids (venue categories). */
export function preloadIcons(ids) {
  const sets = new Set(
    ids.map((id) => parseIconId(id)?.set).filter(Boolean),
  );
  return Promise.all([...sets].map(ensureSet));
}

/** Standalone <svg> markup for an icon record — used to rasterise onto canvas. */
export function iconSvgMarkup(icon, color = "#ffffff") {
  const paint =
    icon.s === "s"
      ? `fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"`
      : `fill="${color}"`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${icon.v}" ${paint}>${icon.b}</svg>`;
}

/** Case-insensitive search over names + keywords. Returns icon names. */
export function searchSet(catalog, query, limit = 400) {
  const entries = Object.keys(catalog ?? {});
  const q = query.trim().toLowerCase();
  if (!q) return entries.slice(0, limit);
  const terms = q.split(/\s+/);
  const scored = [];
  for (const name of entries) {
    const hay = `${name} ${catalog[name].k}`;
    if (!terms.every((t) => hay.includes(t))) continue;
    // Exact name, then prefix, then anything — keeps "bar" above "barrier".
    const score = name === q ? 0 : name.startsWith(q) ? 1 : 2;
    scored.push([score, name]);
  }
  scored.sort((a, b) => a[0] - b[0] || a[1].localeCompare(b[1]));
  return scored.slice(0, limit).map(([, name]) => name);
}
